/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ensureCozeUcSession } from '../coze/ensure-coze-uc-session';
import {
  buildCozeAiQueryLauncher,
  buildCozeChatReturnTo,
  getCozeEmbedEnv,
  isAbrWebchatDirectEmbedEnabled,
  runAiQueryOidcHandshake,
  validateAiQueryOidcConfig,
  type CozeChatQuery,
} from '../ai-query-oidc';

export interface StartCozeAbrChatEmbedOptions {
  iframe: HTMLIFrameElement;
  query: CozeChatQuery;
  onHandshakeComplete?: () => void;
  onTimeout?: () => void;
}

function startDirectCozeAbrChatEmbed(
  options: StartCozeAbrChatEmbedOptions,
): (() => void) | null {
  const env = getCozeEmbedEnv();
  const appOrigin = (env.VITE_ABR_WEBCHAT_URL ?? '').replace(/\/$/, '');
  if (!appOrigin) {
    if (import.meta.env.DEV) {
      console.warn(
        '[abr-chat-frame] direct embed: missing VITE_ABR_WEBCHAT_URL',
      );
    }
    return null;
  }

  const href = buildCozeChatReturnTo(appOrigin, options.query);
  const { iframe } = options;

  const onLoad = () => {
    options.onHandshakeComplete?.();
  };

  iframe.addEventListener('load', onLoad);
  iframe.src = href;

  if (import.meta.env.DEV) {
    console.info('[abr-chat-frame] direct embed /chat/agent', { href });
  }

  return () => {
    iframe.removeEventListener('load', onLoad);
  };
}

/**
 * 嵌入 abr-webchat：默认 IdP launcher + postMessage 桥接；
 * `VITE_ABR_WEBCHAT_DIRECT_EMBED=true` 时 iframe 直连 `/chat/agent`（跳过 IdP）。
 */
export function startCozeAbrChatEmbed(
  options: StartCozeAbrChatEmbedOptions,
): (() => void) | null {
  if (isAbrWebchatDirectEmbedEnabled()) {
    return startDirectCozeAbrChatEmbed(options);
  }

  const env = getCozeEmbedEnv();
  const launcher = buildCozeAiQueryLauncher(env, options.query);
  const validation = validateAiQueryOidcConfig(launcher.config);
  if (validation.ok === false) {
    if (import.meta.env.DEV) {
      console.warn('[abr-chat-frame] OIDC config invalid', validation);
    }
    return null;
  }

  const session = ensureCozeUcSession();
  if (!session) {
    if (import.meta.env.DEV) {
      console.warn(
        '[abr-chat-frame] missing UC session: Authorization cookie not found',
      );
    }
    return null;
  }

  const dispose = runAiQueryOidcHandshake({
    idpOrigin: launcher.idpOrigin,
    token: session.userkey,
    getMessageTarget: () => options.iframe.contentWindow,
    onHandshakeComplete: () => {
      options.onHandshakeComplete?.();
    },
    onTimeout: () => {
      options.onTimeout?.();
    },
  });

  options.iframe.src = launcher.href;

  if (import.meta.env.DEV) {
    console.info('[abr-chat-frame] IdP bridge embed', {
      launcher: launcher.href,
      returnTo: launcher.config.returnTo,
    });
  }

  return dispose;
}
